'use client';

import React from 'react';
import { Grid, GridItem, Heading, Badge, Text, HStack, Box } from '@shared/ui';
import { Picture } from 'app/components/Picture';
import i18n from '@shared/i18n';
import { type CartGiveawayProductCardType } from './types';
import { ProductCounter } from './ProductCounter';

type CartGiveawayCardProps = CartGiveawayProductCardType & {
    specInfo?: string | null;
    onChange: (id: string, quantity: number) => void;
    onDelete?: (id: string) => void;
    addOnLimit?: number;
    isAddonProduct?: boolean;
    isSoldOut?: boolean;
    showQuantityOnly?: boolean;
    counterDisabled?: boolean;
};

const CartGiveawayCardGridTemplateAreas = `
    'product-image product-name'
    'product-image product-bottom'
`;

export const GiveawayCard: React.FC<CartGiveawayCardProps> = (props) => {
    const {
        productId,
        name,
        badge,
        discountedPrice,
        price,
        quantity,
        image,
        onChange,
        addOnLimit,
        isAddonProduct = false,
        isSoldOut,
        showQuantityOnly,
        counterDisabled,
    } = props;

    const renderQuantity = () => {
        if (isSoldOut) {
            return (
                <Text fontWeight="bold" color="neutral.080">
                    {i18n.gettext('已售完')}
                </Text>
            );
        }

        if (showQuantityOnly) {
            return (
                <Text fontWeight="bold" color="neutral.120">
                    x {quantity}
                </Text>
            );
        }

        return (
            <ProductCounter
                id={productId}
                quantity={quantity}
                onChange={onChange}
                max={addOnLimit}
                isDisabled={counterDisabled}
            />
        );
    };

    return (
        <Box w="100%">
            <Grid
                gridTemplateAreas={CartGiveawayCardGridTemplateAreas}
                gridTemplateColumns="auto 1fr"
                gap="xs"
                py="xxs"
                overflow="hidden"
            >
                <GridItem area="product-image">
                    <Box
                        w={isAddonProduct ? '80px' : '72px'}
                        h={isAddonProduct ? '80px' : '72px'}
                        overflow="hidden"
                        position="relative"
                    >
                        <Picture src={image.src} alt={name} width={100} height={100} />
                        {badge && (
                            <Badge
                                position="absolute"
                                top="2px"
                                left="2px"
                                bg="secondary.040"
                                color="neutral.000"
                                fontSize="base"
                                fontWeight="bold"
                            >
                                {badge}
                            </Badge>
                        )}
                    </Box>
                </GridItem>
                <GridItem noOfLines={2} area="product-name">
                    <Heading as="h3" fontSize="sm" fontWeight="bold" lineHeight="1.4">
                        {name}
                    </Heading>
                </GridItem>
                <GridItem area="product-bottom">
                    <Grid gridTemplateAreas="'quantity price'">
                        <GridItem area="quantity">{renderQuantity()}</GridItem>
                        <GridItem area="price" alignContent="center">
                            <HStack
                                justifySelf="end"
                                gap="xs"
                                color="neutral.120"
                                fontWeight="bold"
                                fontSize="sm"
                                flexDirection="row-reverse"
                                alignItems="flex-end"
                            >
                                {discountedPrice && (
                                    <Text
                                        as="strong"
                                        sx={{
                                            '& ~ strong': {
                                                textDecoration: 'line-through',
                                                color: 'neutral.060',
                                                fontSize: 'base',
                                                mb: 'least',
                                            },
                                        }}
                                    >
                                        {typeof discountedPrice === 'string'
                                            ? discountedPrice
                                            : discountedPrice.value}
                                    </Text>
                                )}
                                {price && (
                                    <Text as="strong">
                                        {typeof price === 'string' ? price : price.value}
                                    </Text>
                                )}
                            </HStack>
                        </GridItem>
                    </Grid>
                </GridItem>
            </Grid>
        </Box>
    );
};
